import React from 'react'
import {
  ZoomIn,
  ZoomOut,
  Undo2,
  Redo2,
  Save,
  RotateCcw,
  Plus,
  Trash2,
  Grid3x3,
  Play,
  Pause,
  SkipBack,
  SkipForward
} from 'lucide-react'
import { Button } from '../ui'
import { cn } from '../../lib/utils'

/**
 * TimelineToolbar Component
 * Toolbar with controls for zoom, undo/redo, save, and other operations
 */
const TimelineToolbar = ({
  zoom,
  onZoomIn,
  onZoomOut,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  hasUnsavedChanges,
  onSave,
  onReset,
  selectedBlockIndex,
  onAddLyric,
  onDeleteSelected,
  snapToGrid,
  onToggleSnap,
  isSaving = false,
  // Playback controls
  isPlaying = false,
  onPlay,
  onPause,
  onSkipBack,
  onSkipForward,
  currentTime = 0,
  duration = 0,
  canSkipBack = false,
  canSkipForward = false,
  audioEngineReady = true,
  audioError = false
}) => {
  const zoomLevels = [25, 50, 75, 100, 150, 200, 300, 400]

  const handleZoomIn = () => {
    const currentIndex = zoomLevels.findIndex(level => level >= zoom)
    if (currentIndex < zoomLevels.length - 1) {
      const nextZoom = zoomLevels[currentIndex + 1]
      onZoomIn(nextZoom)
    }
  }

  const handleZoomOut = () => {
    const currentIndex = zoomLevels.findIndex(level => level >= zoom)
    if (currentIndex > 0) {
      const prevZoom = zoomLevels[currentIndex - 1]
      onZoomOut(prevZoom)
    }
  }

  const canZoomIn = zoom < zoomLevels[zoomLevels.length - 1]
  const canZoomOut = zoom > zoomLevels[0]

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-black/40 backdrop-blur-sm border-b border-white/10">
      {/* Left side - Playback & Editing controls */}
      <div className="flex items-center gap-2">
        {/* Playback Controls */}
        <Button
          onClick={onSkipBack}
          size="sm"
          variant="glass"
          disabled={!canSkipBack}
          title="Previous song"
        >
          <SkipBack className="w-4 h-4" />
        </Button>

        <Button
          onClick={isPlaying ? onPause : onPlay}
          size="sm"
          variant="glass"
          disabled={!audioEngineReady || audioError}
          title={isPlaying ? 'Pause' : 'Play'}
          className="w-10 h-10"
        >
          {isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5" />
          )}
        </Button>

        <Button
          onClick={onSkipForward}
          size="sm"
          variant="glass"
          disabled={!canSkipForward}
          title="Next song"
        >
          <SkipForward className="w-4 h-4" />
        </Button>

        {/* Time Display */}
        <div className="px-3 py-1 bg-black/40 rounded text-white text-sm font-medium min-w-[100px] text-center">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>

        <div className="w-px h-6 bg-white/20 mx-1" />

        {/* Editing Controls */}
        {/* Add Lyric */}
        <Button
          onClick={onAddLyric}
          size="sm"
          variant="glass"
          className="flex items-center gap-2"
          title="Add new lyric block"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Add Lyric</span>
        </Button>

        {/* Delete Selected */}
        <Button
          onClick={onDeleteSelected}
          size="sm"
          variant="glass"
          disabled={selectedBlockIndex === null}
          className="flex items-center gap-2"
          title="Delete selected block (Delete/Backspace)"
        >
          <Trash2 className="w-4 h-4" />
          <span className="hidden sm:inline">Delete</span>
        </Button>

        <div className="w-px h-6 bg-white/20 mx-1" />

        {/* Undo/Redo */}
        <Button
          onClick={onUndo}
          size="sm"
          variant="glass"
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
        >
          <Undo2 className="w-4 h-4" />
        </Button>

        <Button
          onClick={onRedo}
          size="sm"
          variant="glass"
          disabled={!canRedo}
          title="Redo (Ctrl+Shift+Z)"
        >
          <Redo2 className="w-4 h-4" />
        </Button>
      </div>

      {/* Center - Zoom controls */}
      <div className="flex items-center gap-2">
        <Button
          onClick={handleZoomOut}
          size="sm"
          variant="glass"
          disabled={!canZoomOut}
          title="Zoom out"
        >
          <ZoomOut className="w-4 h-4" />
        </Button>

        <div className="px-3 py-1 bg-black/40 rounded text-white text-sm font-medium min-w-[60px] text-center">
          {zoom}%
        </div>

        <Button
          onClick={handleZoomIn}
          size="sm"
          variant="glass"
          disabled={!canZoomIn}
          title="Zoom in"
        >
          <ZoomIn className="w-4 h-4" />
        </Button>

        <div className="w-px h-6 bg-white/20 mx-1" />

        {/* Snap to grid */}
        <Button
          onClick={onToggleSnap}
          size="sm"
          variant="glass"
          className={cn(
            'flex items-center gap-2',
            snapToGrid && 'bg-blue-600/30 border-blue-500/50'
          )}
          title="Toggle snap to grid"
        >
          <Grid3x3 className="w-4 h-4" />
          <span className="hidden sm:inline">Snap</span>
        </Button>
      </div>

      {/* Right side - Save controls */}
      <div className="flex items-center gap-2">
        <Button
          onClick={onReset}
          size="sm"
          variant="ghost"
          disabled={!hasUnsavedChanges}
          className="flex items-center gap-2 text-white/80 hover:text-white"
          title="Reset to original lyrics"
        >
          <RotateCcw className="w-4 h-4" />
          <span className="hidden sm:inline">Reset</span>
        </Button>

        <Button
          onClick={onSave}
          size="sm"
          disabled={!hasUnsavedChanges || isSaving}
          className={cn(
            'flex items-center gap-2',
            hasUnsavedChanges
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-white/10 text-white/40'
          )}
          title="Save changes (Ctrl+S)"
        >
          <Save className="w-4 h-4" />
          <span className="hidden sm:inline">
            {isSaving ? 'Saving...' : hasUnsavedChanges ? 'Save Changes' : 'Saved'}
          </span>
        </Button>
      </div>
    </div>
  )
}

export default TimelineToolbar
